import { newUuid } from '@overlayqa/shared';
import { capture } from '../lib/analytics';
import { AUTH_CALLBACK_PENDING_TTL_MS, sessionStorageOrNull } from './authCallbackPending';

type Method = 'email_password' | 'google';
type Stage = 'sign_in' | 'sign_up' | 'second_factor' | 'email_verification' | 'oauth';
type Outcome = 'failed' | 'verification_required' | 'redirect_started' | 'incomplete';
interface Attempt {
  attempt_id: string;
  method: Method;
  started_at: number;
  completion?: { at: number; session_id: string | null };
}
export const DASHBOARD_AUTH_ATTEMPT_KEY = 'oqa_dashboard_auth_attempt';
let memoryAttempt: Attempt | null = null;

export function clearDashboardAuthAttempt(): void {
  memoryAttempt = null;
  try { sessionStorageOrNull()?.removeItem(DASHBOARD_AUTH_ATTEMPT_KEY); } catch { /* no-op */ }
}

function read(): Attempt | null {
  try {
    const store = sessionStorageOrNull();
    if (!store) return memoryAttempt;
    const value = JSON.parse(store.getItem(DASHBOARD_AUTH_ATTEMPT_KEY) ?? 'null');
    if (!value || typeof value.attempt_id !== 'string' || typeof value.started_at !== 'number'
      || !['email_password', 'google'].includes(value.method)) return null;
    return value;
  } catch { return memoryAttempt; }
}

function write(attempt: Attempt): void {
  memoryAttempt = attempt;
  try { sessionStorageOrNull()?.setItem(DASHBOARD_AUTH_ATTEMPT_KEY, JSON.stringify(attempt)); }
  catch { /* Storage restrictions must not block sign-in. */ }
}

function properties(attempt: Attempt) {
  return { surface: 'dashboard', attempt_id: attempt.attempt_id, method: attempt.method };
}

export function beginDashboardAuthAttempt(method: Method): void {
  const attempt: Attempt = { attempt_id: newUuid(), method, started_at: Date.now() };
  write(attempt);
  capture('auth_started', properties(attempt));
}

export function failDashboardOAuthAttempt(errorCode: string): void {
  if (read()?.method === 'google') reportDashboardAuthOutcome('failed', 'oauth', errorCode);
}

export function reportDashboardAuthOutcome(outcome: Outcome, stage: Stage, errorCode?: string | null): void {
  const attempt = read();
  // Only known Clerk code syntax, never error messages, identifiers, passwords or codes.
  const error_code = errorCode && /^[a-z][a-z0-9_]{0,79}$/.test(errorCode) ? errorCode : 'unknown';
  capture('auth_attempt_result', {
    ...(attempt ? properties(attempt) : { surface: 'dashboard' }), outcome, stage,
    ...(outcome === 'failed' ? { error_code } : {}),
  });
  if (attempt && outcome === 'failed') {
    delete attempt.completion;
    write(attempt);
  }
}

export function reportDashboardAuthVerification(stage: 'second_factor' | 'email_verification'): void {
  const attempt = read();
  capture('auth_verification_submitted', { ...(attempt ? properties(attempt) : { surface: 'dashboard' }), stage });
}

// useAuthCardSubmit calls this BEFORE setActive: Clerk can unload the document
// during activation (the same mechanism documented in authCallbackPending.ts).
export function expectDashboardAuthCompletion(sessionId: string | null): void {
  const attempt = read();
  if (!attempt) return;
  attempt.completion = { at: Date.now(), session_id: sessionId };
  write(attempt);
}

/** Called only after App observes a real, hydrated Clerk session. Read/remove once. */
export function completeDashboardAuthAttempt(sessionId: string): void {
  const attempt = read();
  if (!attempt?.completion) return;
  const age = Date.now() - attempt.completion.at;
  if (age < 0 || age > AUTH_CALLBACK_PENDING_TTL_MS) {
    clearDashboardAuthAttempt();
    return;
  }
  if (attempt.completion.session_id && attempt.completion.session_id !== sessionId) return;
  clearDashboardAuthAttempt();
  capture('auth_attempt_result', {
    ...properties(attempt), outcome: 'succeeded', stage: 'session_active',
    time_to_resolve_ms: Math.max(0, Date.now() - attempt.started_at),
  });
}
